<template>
  <div>
    <div class="mb-1">
      <Label label="Submital relacionado" />
    </div>

    <div class="d-flex justify-content-center w-full">
      <SelectableCard
        emptyTitle="No hay ningún submital relacionado"
        :selected="!!relatedSubmital.id"
        @open-selectable="openModalSelector"
      >
        <template #title>
          <span v-if="relatedSubmital.id">{{ relatedSubmital.submital_no || relatedSubmital.acceso_id || '-' }}</span>
          <span v-else>Seleccione un submital</span>
        </template>
        <template #selected-content>
          <table class="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td class="font-weight-bold">Nombre</td>
                <td>{{ relatedSubmital.title || relatedSubmital.nombre || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Cantidad</td>
                <td>{{ relatedSubmital.quantity || relatedSubmital.cantidad || '-' }}</td>
              </tr>
            </tbody>
          </table>
          <div class="mt-3 self-align-end">
            <Button
              variant="blue"
              :disabled="submitalLocked"
              @click="openModalSelector"
            >
              Cambiar
            </Button>
          </div>
        </template>
      </SelectableCard>
    </div>

    <!-- Modal selector -->
    <Modal
      v-if="showModalSelector"
      contentWidth="750px"
      :required=true
      @close="hideModalSelector"
    >
      <template #header>
        <p class="fs-20 font-weight-bold ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
          Submitals del proyecto
        </p>
      </template>
      <template #body>
        <b-row align-h="center">
          <b-col cols="12">
            <b-table
              hover
              bordered
              small
              responsive
              selectable
              select-mode="single"
              :busy="processing"
              :items="selectableConfigurables"
              :fields="fields"
            >
              <template #table-busy>
                <div class="text-center text-info my-3">
                  <b-spinner small class="mr-2" />
                  Cargando submitals
                </div>
              </template>

              <template #cell(related_configurable)="data">
                <div v-if="data.item.relationship_id" class="fs-14">
                  <span class="text-info">
                    <font-awesome-icon :icon="['far', 'info-circle']" fixed-width />
                    Relacionado con este submital
                  </span>
                </div>
                <div v-else-if="data.item.related_configurable" class="fs-14">
                  <span v-if="data.item.related_configurable.submital_no">{{ data.item.related_configurable.submital_no }}</span>
                  <span v-else>{{ data.item.related_configurable.id }}</span>
                </div>
              </template>

              <template #cell(actions)="data">
                <div class="d-flex flex-column justify-content-center">
                  <Button
                    v-if="data.item.relationship_id"
                    size="sm"
                    variant="red"
                    :disabled="processingRelationship"
                    @click="removeRelationship(data.item)"
                  >
                    Quitar
                  </Button>
                  <Button
                    v-else-if="!data.item.related_configurable && !relatedSubmital.id"
                    size="sm"
                    variant="blue"
                    :disabled="processingRelationship"
                    @click="createRelationship(data.item)"
                  >
                    Asociar
                  </Button>
                </div>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </template>
      <template #footer>
        <span></span>
      </template>
    </Modal>
  </div>
</template>

<script>
import Button from '../../shared/Button'
import Modal from '../../shared/Modal.vue'
import Label from '../configurators/Label'
import SelectableCard from '../configurators/SelectableCard'
import { mapGetters } from 'vuex'

export default {
  name: 'RelatedSubmitalSelector',
  components: { Button, Modal, Label, SelectableCard },
  props: {
    currentConfigurableType: {
      type: String,
      required: true
    },
    currentConfigurableId: {
      type: Number,
      required: true
    },
    relatedSubmital: {
      type: Object,
      default: () => {}
    },
    project: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      showModalSelector: false,
      availableConfigurables: [],
      processing: false,
      fields: [
        { key: 'submital_no', label: 'Submital' },
        { key: 'title', label: 'Nombre' },
        { key: 'quantity', label: 'Cantidad', class: 'text-center' },
        { key: 'related_configurable', label: 'Asociado con', class: 'text-center' },
        { key: 'actions', label: '', class: 'text-center' }
      ],
      processingRelationship: false
    }
  },
  computed: {
    ...mapGetters(['submitalLocked']),
    selectableConfigurables () {
      return this.availableConfigurables
    }
  },
  methods: {
    getAvailableConfigurables () {
      if (this.processing || !this.currentConfigurableId || !this.currentConfigurableType || !this.project.id) return

      this.processing = true
      this.$http.get(`/api/production/projects/${this.project.id}/submital_relationships`, {
          params: {
            current_configurable_type: this.currentConfigurableType,
            current_configurable_id: this.currentConfigurableId
          }
        })
        .then((res) => {
          this.availableConfigurables = res.data
        })
        .catch(e => console.log(e))
        .finally(() => this.processing = false)
    },
    openModalSelector () {
      this.showModalSelector = true
      this.getAvailableConfigurables()
    },
    hideModalSelector () {
      this.showModalSelector = false
    },
    createRelationship (item) {
      if (this.processingRelationship) return

      this.processingRelationship = true
      this.$http.post(`/api/production/projects/${this.project.id}/submital_relationships`, {
          submital_relationship: {
            configurable_a_id: this.currentConfigurableId,
            configurable_a_type: this.currentConfigurableType,
            configurable_b_id: item.configurable_id,
            configurable_b_type: item.configurable_type
          }
        })
        .then((res) => {
          this.updateAvailableConfigurable(item, { relationship_id: res.data.relationship_id })

          const relatedConfigurable = {
            id: item.configurable_id,
            submital_no: item.submital_no,
            acceso_id: item.submital_no,
            title: item.title,
            nombre: item.title,
            quantity: item.quantity,
            cantidad: item.quantity,
            configurable_type: item.configurable_type,
            relationship_id: res.data.relationship_id
          }
          this.$emit('relationship-changed', relatedConfigurable)
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => this.processingRelationship = false)
    },
    removeRelationship (item) {
      if (this.processingRelationship) return

      this.processingRelationship = true
      this.$http.delete(`/api/production/projects/${this.project.id}/submital_relationships/${item.relationship_id}`)
        .then((res) => {
          this.updateAvailableConfigurable(item, { relationship_id: null })
          this.$emit('relationship-changed', null)
          this.hideModalSelector()
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => this.processingRelationship = false)
    },
    removeCurrentRelationship () {
      if (!this.relatedSubmital || !this.relatedSubmital.relationship_id) return
      this.removeRelationship(this.relatedSubmital)
    },
    updateAvailableConfigurable (item, changes) {
      const index = this.availableConfigurables.findIndex(
        c => c.configurable_id === item.configurable_id && c.configurable_type === item.configurable_type
      )
      if (index === -1) return

      this.availableConfigurables.splice(index, 1, {
        ...this.availableConfigurables[index],
        ...changes
      })
    }
  }
}
</script>
